import { type FeedbackItem, type LinkedElement, type PinPosition } from "@previewloop/shared";
import { type StrokePoint } from "./widget-draw";

/**
 * Unsent feedback survives navigation: collected entries (screenshot blob
 * included) and an in-progress composer draft are mirrored into IndexedDB,
 * keyed by page URL, and restored when the widget mounts on the same page.
 * IndexedDB over localStorage because screenshots are megabyte-scale blobs
 * and the widget is a guest on the customer's origin quota.
 */

export type PersistedEntry = {
  id: string;
  item: FeedbackItem;
  screenshot: Blob;
  pin: PinPosition;
  /** Click position for each linked target, aligned with item.linkedElements. */
  targetPins?: PinPosition[];
  strokes?: StrokePoint[][];
};

export type PersistedDraftTarget = LinkedElement & { pin: PinPosition };

export type PersistedDraft = {
  text: string;
  targets: [PersistedDraftTarget, ...PersistedDraftTarget[]];
};

export type PersistedRecord = {
  savedAt: number;
  entries: PersistedEntry[];
  draft?: PersistedDraft;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const hasLinkedElements = (entry: unknown): boolean => {
  if (!isObject(entry) || !isObject(entry.item)) return false;
  const linked = entry.item.linkedElements;
  return Array.isArray(linked) && linked.length > 0;
};

/**
 * Storage is a boundary: a record written by an older widget build (single-target
 * entries and drafts) or a corrupt one must not reach the store. Entries and drafts
 * that lack a linked-element collection are dropped; a record with nothing left is null.
 */
export const parsePersistedRecord = (value: unknown): PersistedRecord | null => {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Partial<PersistedRecord>;
  if (typeof record.savedAt !== "number" || !Array.isArray(record.entries)) return null;
  const entries = record.entries.filter(hasLinkedElements);
  const draft =
    isObject(record.draft) && Array.isArray(record.draft.targets) && record.draft.targets.length > 0
      ? record.draft
      : undefined;
  if (entries.length === 0 && !draft) return null;
  return { savedAt: record.savedAt, entries, draft };
};

export type UnsentPersistence = {
  load: (pageKey: string) => Promise<PersistedRecord | null>;
  save: (pageKey: string, record: PersistedRecord) => Promise<void>;
  clear: (pageKey: string) => Promise<void>;
};

/** Preview deploys are short-lived; day-old feedback is stale anyway. */
const TTL_MS = 24 * 60 * 60 * 1000;

const DB_NAME = "previewloop-widget";
const STORE_NAME = "unsent";

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.addEventListener("error", () => reject(request.error));
  });

const withStore = async <T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.addEventListener("error", () => reject(request.error));
    });
  } finally {
    db.close();
  }
};

/** Drop every expired record, not just the current page's. */
const purgeExpired = async (): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve) => {
      const store = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME);
      const cursorRequest = store.openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor) {
          resolve();
          return;
        }
        const record = cursor.value as PersistedRecord;
        if (Date.now() - record.savedAt > TTL_MS) cursor.delete();
        cursor.continue();
      };
      cursorRequest.addEventListener("error", () => resolve());
    });
  } finally {
    db.close();
  }
};

/**
 * IndexedDB-backed persistence. Every operation degrades to a no-op when
 * IndexedDB is unavailable (private modes, test DOMs) - losing persistence
 * must never break the widget itself.
 */
export function createIdbPersistence(): UnsentPersistence {
  const available = typeof indexedDB !== "undefined";
  // Once per instance: on an SPA, load runs per navigation, and a full-store
  // cursor walk over screenshot-sized blobs is not a per-route cost.
  let purged = false;
  return {
    load: async (pageKey) => {
      if (!available) return null;
      try {
        if (!purged) {
          purged = true;
          void purgeExpired().catch(() => {});
        }
        const record = await withStore<PersistedRecord | undefined>("readonly", (store) =>
          store.get(pageKey),
        );
        if (!record) return null;
        if (Date.now() - record.savedAt > TTL_MS) return null;
        return record;
      } catch {
        return null;
      }
    },
    save: async (pageKey, record) => {
      if (!available) return;
      try {
        await withStore("readwrite", (store) => store.put(record, pageKey));
      } catch {
        /* quota or blocked IDB - persistence is best-effort */
      }
    },
    clear: async (pageKey) => {
      if (!available) return;
      try {
        await withStore("readwrite", (store) => store.delete(pageKey));
      } catch {
        /* ignore */
      }
    },
  };
}

/** The page identity a record is stored under. Hash ignored - pins are
    page-bound coordinates. One definition, shared by production and tests:
    stored records are a contract with this rule. */
export const pageKeyFor = (loc: Location): string => loc.origin + loc.pathname + loc.search;

export type UnsentMirrorOptions = {
  persistence: UnsentPersistence;
  /** Computed per call: on an SPA the URL can change under a long-lived widget. */
  pageKey: () => string;
  /** Current unsent state; null means nothing unsent (the record is cleared). */
  snapshot: () => PersistedRecord | null;
  /** Apply a loaded record. Never called after dispose. */
  onRestore: (record: PersistedRecord) => void;
};

export type UnsentMirror = {
  /** Debounced write - typing must not write a screenshot-sized record per keystroke. */
  schedule: () => void;
  /** Immediate write - for moments where the debounce window is a liability
      (a record left behind after send would restore as a duplicate). */
  flush: () => void;
  /** The URL changed under a live widget (SPA navigation). Persists the current
      snapshot under the page it belongs to, then restores the new page's record
      as a fresh mount would. Call before resetting page-bound state - the
      snapshot must still see the departed page's entries. Returns false when
      the page key did not actually change. */
  pageChange: () => boolean;
  dispose: () => void;
};

/**
 * Write-through mirror of the unsent batch: debounces writes, flushes when the
 * page hides, and restores once on creation. No writes happen until the
 * pending restore resolved - an early clear (e.g. a draft closed right after
 * mount) must not wipe the record being read. A hung IndexedDB open (a known
 * Safari failure mode) is bounded by a 2s race that gives up on restoring,
 * never on persisting.
 */
export function createUnsentMirror(options: UnsentMirrorOptions): UnsentMirror {
  const { persistence, pageKey, snapshot, onRestore } = options;
  let timer: ReturnType<typeof setTimeout> | undefined;
  /** The page this mirror's in-memory state belongs to. */
  let currentKey = pageKey();
  /** True once the restore for currentKey landed; writes stay gated until then. */
  let restoreSettled = false;
  /** Whether storage may hold a record for currentKey - a session that never
      collected anything must not pay a delete transaction per navigation. */
  let hadRecord = false;
  let disposed = false;

  const write = (key = currentKey): void => {
    timer = undefined;
    const record = snapshot();
    if (record) {
      // Live work always persists - even while the restore for this page is
      // still in flight, losing a just-written comment is worse than
      // overwriting a record the restore is about to merge in anyway.
      hadRecord = true;
      void persistence.save(key, record);
    } else if (restoreSettled && hadRecord) {
      // Clears wait for the restore: an early clear (e.g. a draft closed
      // right after mount) must not wipe the record being read - and a
      // session that never persisted anything skips the transaction.
      hadRecord = false;
      void persistence.clear(key);
    }
  };

  const schedule = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => write(), 300);
  };

  const flush = (): void => {
    if (timer) clearTimeout(timer);
    write();
  };

  /** The page is going away (or hidden, the earlier and more reliable
      signal) - flush whatever the debounce is still holding. */
  const onPageHide = (): void => {
    if (!timer) return;
    clearTimeout(timer);
    write();
  };
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onPageHide);

  /** Monotonic per-restore token: a stale load (the page changed again while
      one was in flight, even back to the same URL) must apply at most once
      and never to a later page's state. */
  let generation = 0;

  const restore = (): void => {
    const started = ++generation;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const loadTimeout = new Promise<null>((resolve) => {
      timeoutId = setTimeout(() => resolve(null), 2000);
    });
    void Promise.race([persistence.load(currentKey), loadTimeout])
      .then((loaded) => {
        if (started !== generation) return;
        restoreSettled = true;
        if (disposed) return;
        const record = loaded ? parsePersistedRecord(loaded) : null;
        if (!record) return;
        hadRecord = true;
        onRestore(record);
      })
      .catch(() => {
        if (started === generation) restoreSettled = true;
      })
      .finally(() => clearTimeout(timeoutId));
  };
  restore();

  const pageChange = (): boolean => {
    const next = pageKey();
    if (next === currentKey) return false;
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    write(currentKey);
    currentKey = next;
    restoreSettled = false;
    hadRecord = false;
    restore();
    return true;
  };

  return {
    schedule,
    flush,
    pageChange,
    dispose: () => {
      disposed = true;
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onPageHide);
      if (timer) clearTimeout(timer);
    },
  };
}
